import {
  ActionIcon,
  Badge,
  Button,
  Divider,
  Flex,
  Group,
  Image,
  Loader,
  Menu,
  Modal,
  Text,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import {
  IconCertificate,
  IconDots,
  IconDownload,
  IconFile,
  IconFileCheck,
  IconFileText,
  IconLink,
  IconRefresh,
  IconSignature,
  IconTrash,
} from '@tabler/icons';
import axios from 'axios';
import { useState } from 'react';
import { api } from '../../providers/base-api';
import { regeneratePayslipLinkRequest } from '../../data/employee-payslip';
import {
  errorNotification,
  successNotification,
} from '../../providers/mantine-notifications';
import { useDeletePayslip } from '../../data/hooks/employee-payslip';
import { EmployeePayslip } from '../../models/employee-payslip';
import { File } from '../../models/file';
import { formatDateTime } from '../../utils/helpers';

interface CardFileProps {
  item: File;
}

interface CardPayslipFileProps {
  item: EmployeePayslip;
  onSignedDocumentCreated?: () => void;
  onDeleted?: () => void;
}

async function downloadFile(fileUrl: string, fileName: string) {
  const { data: blob } = await axios.get(fileUrl, {
    responseType: 'blob',
    headers: {},
  });
  const url = URL.createObjectURL(new Blob([blob]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  URL.revokeObjectURL(url);
  document.body.removeChild(link);
}

export function CardFile({ item }: CardFileProps) {
  const isPdf = item.ext === 'pdf';

  return (
    <Flex
      align="center"
      gap="sm"
      py="xs"
      px="sm"
      mb={4}
      style={{
        border: '1px solid #e9ecef',
        borderRadius: 8,
        background: '#fff',
      }}
    >
      <ThemeIcon variant="light" color="blue" size="lg" radius="md">
        {isPdf ? <IconFileText size={18} /> : <IconFile size={18} />}
      </ThemeIcon>

      <Flex direction="column" style={{ flex: 1, minWidth: 0 }}>
        <Text size="sm" fw={500} lineClamp={1}>
          {item.name}
        </Text>
        <Text size="xs" color="dimmed">
          {item.created_at ? formatDateTime(item.created_at) : '--'}
          {item.createdBy ? ` · ${item.createdBy}` : ''}
        </Text>
      </Flex>

      {!isPdf && (
        <Image
          src={item.url}
          width={48}
          height={40}
          radius="sm"
          fit="cover"
          withPlaceholder
        />
      )}

      <Tooltip label="Download" withArrow>
        <ActionIcon
          variant="light"
          color="blue"
          onClick={() => downloadFile(item.url, item.name)}
        >
          <IconDownload size={16} />
        </ActionIcon>
      </Tooltip>
    </Flex>
  );
}

export function CardPayslipFile({
  item,
  onSignedDocumentCreated,
  onDeleted,
}: CardPayslipFileProps) {
  const isSigned = !!item.signedAt;
  const [generatingDoc, setGeneratingDoc] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { fetch: deletePayslip, loading: deleting } = useDeletePayslip();

  async function handleGenerateSignedDocument() {
    setGeneratingDoc(true);
    try {
      await api.post(`/employee-payslip/${item.id}/signed-document`);
      onSignedDocumentCreated?.();
    } finally {
      setGeneratingDoc(false);
    }
  }

  async function handleRegenerateLink() {
    try {
      const { data } = await regeneratePayslipLinkRequest(item.id);
      await navigator.clipboard.writeText(data.url);
      successNotification({
        title: 'Novo link copiado.',
        message: 'O link anterior deixou de funcionar.',
      });
    } catch {
      errorNotification({
        title: 'Erro ao gerar novo link.',
        message: 'Tente novamente.',
      });
    }
  }

  return (
    <Flex
      direction="column"
      mb={8}
      style={{
        border: `1px solid ${isSigned ? '#d3f9d8' : '#e9ecef'}`,
        borderRadius: 10,
        background: isSigned ? '#f8fffe' : '#fff',
        overflow: 'hidden',
      }}
    >
      {/* Header row */}
      <Flex align="center" gap="sm" px="sm" py="xs">
        <ThemeIcon
          variant="light"
          color={isSigned ? 'teal' : 'gray'}
          size="lg"
          radius="md"
        >
          {isSigned ? <IconFileCheck size={18} /> : <IconFileText size={18} />}
        </ThemeIcon>

        <Flex direction="column" style={{ flex: 1, minWidth: 0 }}>
          <Text size="sm" fw={500} lineClamp={1}>
            {item.documentFile.name}
          </Text>
          <Text size="xs" color="dimmed">
            {item.createdAt ? formatDateTime(item.createdAt) : '--'}
            {item.createdBy ? ` · ${item.createdBy}` : ''}
          </Text>
        </Flex>

        <Group spacing={6} noWrap>
          <Badge color={isSigned ? 'teal' : 'gray'} variant="light" size="sm">
            {isSigned ? 'Assinado' : 'Pendente'}
          </Badge>

          <Menu withinPortal position="bottom-end" shadow="sm">
            <Menu.Target>
              <ActionIcon size="sm" variant="subtle" color="gray">
                <IconDots size={14} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                icon={<IconDownload size={14} />}
                onClick={() =>
                  downloadFile(item.documentFile.url, item.documentFile.name)
                }
              >
                Holerite original
              </Menu.Item>
              {!isSigned && (
                <Menu.Item
                  icon={<IconLink size={14} />}
                  onClick={handleRegenerateLink}
                >
                  Gerar novo link de assinatura
                </Menu.Item>
              )}
              <Menu.Item
                color="red"
                icon={<IconTrash size={14} />}
                onClick={() => setConfirmDelete(true)}
              >
                Remover
              </Menu.Item>
              {isSigned && (
                <Menu.Item
                  icon={
                    generatingDoc ? (
                      <Loader size={14} />
                    ) : (
                      <IconRefresh size={14} />
                    )
                  }
                  onClick={handleGenerateSignedDocument}
                  disabled={generatingDoc}
                >
                  {item.signedDocumentFile
                    ? 'Regenerar holerite assinado'
                    : 'Gerar holerite assinado'}
                </Menu.Item>
              )}
              {item.signedDocumentFile && (
                <Menu.Item
                  icon={<IconFileCheck size={14} />}
                  onClick={() => {
                    const f = item.signedDocumentFile;
                    if (f) downloadFile(f.url, f.name);
                  }}
                >
                  Holerite assinado
                </Menu.Item>
              )}
              {item.proofFile && (
                <Menu.Item
                  icon={<IconCertificate size={14} />}
                  onClick={() => {
                    const f = item.proofFile;
                    if (f) downloadFile(f.url, f.name);
                  }}
                >
                  Comprovante de assinatura
                </Menu.Item>
              )}
              {item.signatureFile && (
                <Menu.Item
                  icon={<IconSignature size={14} />}
                  onClick={() =>
                    downloadFile(
                      item.signatureFile.url,
                      item.signatureFile.name,
                    )
                  }
                >
                  Imagem da assinatura
                </Menu.Item>
              )}
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Flex>

      <Modal
        opened={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Remover holerite"
        size="sm"
        closeOnClickOutside={!deleting}
        closeOnEscape={!deleting}
      >
        <Text size="sm" mb="lg">
          Tem certeza que deseja remover o holerite{' '}
          <strong>{item.documentFile.name}</strong>? Esta ação não pode ser
          desfeita.
        </Text>
        <Group position="right" spacing="sm">
          <Button
            variant="default"
            onClick={() => setConfirmDelete(false)}
            disabled={deleting}
          >
            Cancelar
          </Button>
          <Button
            color="red"
            loading={deleting}
            onClick={() =>
              deletePayslip({
                id: item.id,
                onSuccess: () => {
                  setConfirmDelete(false);
                  onDeleted?.();
                },
              })
            }
          >
            Remover
          </Button>
        </Group>
      </Modal>

      {/* Signed info */}
      {isSigned && (
        <>
          <Divider color="teal.1" />
          <Flex gap="xl" px="sm" py={6} align="center" wrap="wrap">
            <Flex direction="column">
              <Text size="xs" color="dimmed">
                Assinado em
              </Text>
              <Text size="xs" fw={500}>
                {formatDateTime(item.signedAt)}
              </Text>
            </Flex>
            {item.signedBy && (
              <Flex direction="column">
                <Text size="xs" color="dimmed">
                  Assinado por
                </Text>
                <Text size="xs" fw={500}>
                  {item.signedBy}
                </Text>
              </Flex>
            )}
            <Flex gap={6} ml="auto">
              {isSigned && (
                <Tooltip
                  label={
                    item.signedDocumentFile
                      ? 'Regenerar holerite assinado'
                      : 'Gerar holerite assinado'
                  }
                  withArrow
                >
                  <ActionIcon
                    variant="light"
                    color="orange"
                    size="sm"
                    loading={generatingDoc}
                    onClick={handleGenerateSignedDocument}
                  >
                    <IconRefresh size={14} />
                  </ActionIcon>
                </Tooltip>
              )}
              {item.signedDocumentFile && (
                <Tooltip label="Holerite assinado" withArrow>
                  <ActionIcon
                    variant="light"
                    color="blue"
                    size="sm"
                    onClick={() => {
                      const f = item.signedDocumentFile;
                      if (f) downloadFile(f.url, f.name);
                    }}
                  >
                    <IconFileCheck size={14} />
                  </ActionIcon>
                </Tooltip>
              )}
              {item.proofFile && (
                <Tooltip label="Comprovante" withArrow>
                  <ActionIcon
                    variant="light"
                    color="teal"
                    size="sm"
                    onClick={() => {
                      const f = item.proofFile;
                      if (f) downloadFile(f.url, f.name);
                    }}
                  >
                    <IconCertificate size={14} />
                  </ActionIcon>
                </Tooltip>
              )}
            </Flex>
          </Flex>
        </>
      )}
    </Flex>
  );
}
